import { FC, useRef, useEffect, useState, memo } from 'react';
import * as eCharts from 'echarts';
import { lineTooltipFormatter } from './linechart';
import levelS from '@/pages/intelligence/assets/svg/pcConsoleBenchmark/level-s.svg';
import levelA from '@/pages/intelligence/assets/svg/pcConsoleBenchmark/level-a.svg';
import levelB from '@/pages/intelligence/assets/svg/pcConsoleBenchmark/level-b.svg';
import levelC from '@/pages/intelligence/assets/svg/pcConsoleBenchmark/level-c.svg';
import levelD from '@/pages/intelligence/assets/svg/pcConsoleBenchmark/level-d.svg';
import { set, cloneDeep, get } from 'lodash-es';

const xAxisList = [
  'M1',
  'M2',
  'M3',
  'M4',
  'M5',
  'M6',
  'M7',
  'M8',
  'M9',
  'M10',
  'M11',
];

interface RetentionCurveProps {
  gameRetentionList: { [key: string]: any }[];
}

const RetentionCurve: FC<RetentionCurveProps> = (props) => {
  let myChart: any = null;
  const { gameRetentionList } = props;
  const chartRef = useRef(null);
  const [options, setOptions] = useState<any>();

  const getLevelImage = (type: string) => {
    switch (type) {
      case 'S':
        return levelS;
      case 'A':
        return levelA;
      case 'B':
        return levelB;
      case 'C':
        return levelC;
      case 'D':
        return levelD;
      default:
        return '';
    }
  };

  useEffect(() => {
    let selectedConfig: any = {};
    let gameListSeries: any[] = [];
    gameRetentionList?.forEach((item, index) => {
      gameListSeries.push({
        name: item?.name,
        type: 'line',
        showSymbol: false,
        symbolSize: 10,
        symbol: 'circle',
        emphasis: {
          itemStyle: {
            borderWidth: 1,
            borderColor: '#fff'
          }
        },
        data:
          item?.curve_metrics?.map((config: any) => ({
            value: config?.metric_value * 100,
            level: config?.metric_rate,
            cover: item?.cover,
          })) || [],
      });
      selectedConfig[item.name] = index <= 5;
    });
    const gameLineOptions = {
      grid: {
        top: 10,
        bottom: 20,
        left: 15,
        right: 27,
        containLabel: true,
      },
      legend: {
        show: true,
        selected: selectedConfig,
        type: 'scroll',
        orient: 'horizontal', //横向
        pageIconColor: '#000', //翻页箭头颜色
        selectedMode: true,
        pageIconSize: 13,
        pageTextStyle: {
          color: '#000', //翻页数字颜色
        }, //翻页数字设置
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        bottom: -6,
        left: 'center',
      },
      tooltip: {
        confine: true, //fix遮挡:让tooltip展示在折线区域内
        trigger: 'axis',
        showDelay: 10,
        hideDelay: 100,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        extraCssText: 'border: none;',
        padding: 0,
        axisPointer: {
          z: 0,
          label: {
            show: true,
            color: '#3B3D4B',
            backgroundColor: '#FFF',
          },
          lineStyle: {
            color: '#BBBDCB',
            type: 'solid'
          }  
        },
        formatter: (param: any) => {
          return lineTooltipFormatter(
            param?.sort((a: any, b: any) => {
              const aCharCodeAt =
                a?.data?.level === 'S' ? 64 : a?.data?.level?.charCodeAt();
              const bCharCodeAt =
                b?.data?.level === 'S' ? 64 : b?.data?.level?.charCodeAt();

              return aCharCodeAt - bCharCodeAt;
            }),
            param[0]?.axisValue,
            'Level',
            (value: any, target: any) => {
              return `<img src="${getLevelImage(
                target?.level,
              )}" width="12px" /> <span style="display: inline-block;width: 10px;">${
                target?.level
              }</span>`;
            },
          );
        },
      },
      xAxis: {
        type: 'category',
        data: xAxisList,
        axisLine: {
          lineStyle: {
            color: '#BFBFBF',
          },
        },
        boundaryGap: false, // 不留白，从原点开始
      },
      yAxis: {
        type: 'value',
        // max: Math.ceil(get(gameListSeries, '0.data.0.value', 0) * 1.1 + 1),
        axisLine: {
          show: false,
          lineStyle: {
            color: '#BFBFBF',
          },
        },
        axisLabel: {
          formatter: '{value}%',
          show: false,
        },
      },
      series: gameListSeries,
    };

    setOptions(gameLineOptions);
  }, [gameRetentionList]);

  const handleResize = () => {
    try {
      myChart.resize();
    } catch (err) {}
  };

  useEffect(() => {
    if (myChart === null) {
      myChart = eCharts.init(chartRef!.current!);
    }
    options && myChart.setOption(options);

    myChart.on('legendselectchanged', (params: any) => {
      const selectedConfig = cloneDeep(params.selected);
      const selectedList = Object.values(params.selected).filter(
        (item) => item,
      );
      const gameOptions = cloneDeep(options);
      if (selectedList.length > 10) {
        set(selectedConfig, `${params.name}`, false);
        set(gameOptions, `legend.selected`, selectedConfig);
        setOptions(gameOptions);
      }
    });
    return () => {
      myChart.off('legendselectchanged');
    };
  }, [options]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [myChart]);

  return <div ref={chartRef} style={{ height: 430, width: '100%' }}></div>;
};

RetentionCurve.defaultProps = {};

export default memo(RetentionCurve);
